import clsx from "clsx";
import { useFormik } from "formik";
import { useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useQueryClient } from "react-query";
import * as Yup from "yup";
import { CreateQuestionSectionData } from "../API/Question_Section_APIs";
import { lookupKeys } from "../../../lib/queries/lookups";

const validationSchema = Yup.object().shape({
  sectionName: Yup.string().required("Section name is required"),
  sectionDescription: Yup.string().required("Section description is required"),
  toBeLinkedWith: Yup.string(),
});

interface QuestionSectionCreateModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess?: () => void; // Optional callback after successful creation
}

const QuestionSectionCreateModal = ({ show, onHide, onSuccess }: QuestionSectionCreateModalProps) => {
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  const initialValues = {
    sectionName: "",
    sectionDescription: "",
    toBeLinkedWith: "",
  };



  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const payload = {
          sectionName: values.sectionName,
          sectionDescription: values.sectionDescription,
          toBeLinkedWith: values.toBeLinkedWith,
        };
        await CreateQuestionSectionData(payload);
        queryClient.invalidateQueries(lookupKeys.questionSections);
        formik.resetForm();
        if (onSuccess) onSuccess(); // Call parent callback
        onHide(); // Close modal
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
  });

  

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Add New Section</Modal.Title>
      </Modal.Header>

      <form onSubmit={formik.handleSubmit}>
        <Modal.Body>
          {/* Section Name */}
          <div className="fv-row mb-4">
            <label className="required fs-6 fw-bold mb-2">Section Name:</label>
            <input
              type="text"
              placeholder="Enter Section Name"
              {...formik.getFieldProps("sectionName")}
              className={clsx(
                "form-control form-control-solid",
                {
                  "is-invalid text-danger": formik.touched.sectionName && formik.errors.sectionName,
                  "is-valid": formik.touched.sectionName && !formik.errors.sectionName,
                }
              )}
            />
            {formik.touched.sectionName && formik.errors.sectionName && (
              <div className="fv-help-block text-danger">
                <span role="alert">{formik.errors.sectionName}</span>
              </div>
            )}
          </div>

          {/* Section Description */}
          <div className="fv-row mb-4">
            <label className="required fs-6 fw-bold mb-2">Section Description:</label>
            <textarea
              placeholder="Enter Section Description"
              rows={3}
              {...formik.getFieldProps("sectionDescription")}
              className={clsx(
                "form-control form-control-solid",
                {
                  "is-invalid text-danger": formik.touched.sectionDescription && formik.errors.sectionDescription,
                  "is-valid": formik.touched.sectionDescription && !formik.errors.sectionDescription,
                }
              )}
            />
            {formik.touched.sectionDescription && formik.errors.sectionDescription && (
              <div className="fv-help-block text-danger">
                <span role="alert">{formik.errors.sectionDescription}</span>
              </div>
            )}
          </div>

          {/* To Be Linked With */}
          <div className="fv-row mb-4">
            <label className="fs-6 fw-bold mb-2">To Be Linked With:</label>
            <textarea
              placeholder="e.g. another section name, assessment, tool, or any related context"
              rows={3}
              {...formik.getFieldProps("toBeLinkedWith")}
              className={clsx(
                "form-control form-control-solid",
                {
                  "is-invalid text-danger": formik.touched.toBeLinkedWith && formik.errors.toBeLinkedWith,
                  "is-valid": formik.touched.toBeLinkedWith && !formik.errors.toBeLinkedWith && !!formik.values.toBeLinkedWith,
                }
              )}
            />
            <div className="form-text" style={{ fontSize: "0.78rem", color: "#64748b", marginTop: 4 }}>
              Optional — note any section, tool, or content this section should connect to.
            </div>
            {formik.touched.toBeLinkedWith && formik.errors.toBeLinkedWith && (
              <div className="fv-help-block text-danger">
                <span role="alert">{formik.errors.toBeLinkedWith}</span>
              </div>
            )}
          </div>


        </Modal.Body>

        <Modal.Footer>
          <div className="d-flex justify-content-end w-100">
            <div>
              <Button variant="light" onClick={onHide} className="me-2">
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={formik.isSubmitting || !formik.isValid}>
                {!loading && <span>Create Section</span>}
                {loading && (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Creating...
                  </>
                )}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default QuestionSectionCreateModal;
